import React from 'react';
import { UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faFileLines, faPrint } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { OrderPrintLogView } from '../../../../../services/orders/orders.service';
import { getKindLabel } from './order-print-log.utils';
import classes from './order-print-log-modal.module.scss';

type OrderPrintLogModalProps = {
  opened: boolean;
  logs: OrderPrintLogView[];
  isLoading: boolean;
  title: string;
  onClose: () => void;
  onOpenItems: (log: OrderPrintLogView) => void;
};

const CLOSE_LABEL = 'סגור';
const EMPTY_LABEL = 'אין הדפסות בלוג להזמנה זו';

function getLineCountLabel(lineCount: number): string {
  return `${lineCount} ${lineCount === 1 ? 'שורה' : 'שורות'}`;
}

function formatLogTime(value: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat('he-IL', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(value));
}

function getKindIcon(kind: OrderPrintLogView['kind']): IconProp {
  if (kind === 'receipt_print') return faFileLines as IconProp;
  if (kind === 'red_fire') return faBan as IconProp;
  return faPrint as IconProp;
}

// RED (red_fire) rows have no distinct "skipped" status in the data model — status is always
// 'printed' since the skip action itself fired successfully. Relabel for display only so a red
// דילוג kind badge doesn't sit next to a green "הודפס" that reads as contradictory.
function getStatusLabel(status: OrderPrintLogView['status'], kind: OrderPrintLogView['kind']): string {
  if (status === 'failed') return 'נכשל';
  if (status === 'queued') return 'ממתין';
  if (kind === 'red_fire') return 'דולג';
  if (kind === 'reprint') return 'שליחה חוזרת';
  return 'הודפס';
}

function getStatusClass(status: OrderPrintLogView['status'], kind: OrderPrintLogView['kind']): string {
  if (status === 'failed') return classes.statusFailed;
  if (status === 'queued') return classes.statusQueued;
  if (kind === 'red_fire') return classes.statusFailed;
  if (kind === 'reprint') return classes.statusReprint;
  return classes.statusPrinted;
}

const OrderPrintLogModalComponent: React.FC<OrderPrintLogModalProps> = ({
  opened,
  logs,
  isLoading,
  title,
  onClose,
  onOpenItems,
}) => {
  if (!opened) return null;

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true" aria-label={title} dir="rtl" onClick={onClose}>
      <div className={classes.modal} onClick={(event) => event.stopPropagation()}>
        <header className={classes.header}>
          <div className={classes.titleWrap}>
            <span className={classes.title}>{title}</span>
          </div>
          <UnstyledButton component="button" type="button" className={classes.closeBtn} onClick={onClose} aria-label={CLOSE_LABEL} data-testid="print-log-close">
            ×
          </UnstyledButton>
        </header>

        <div className={classes.content}>
          {isLoading ? (
            <div className={classes.stateLabel}>טוען...</div>
          ) : logs.length === 0 ? (
            <div className={classes.stateLabel}>{EMPTY_LABEL}</div>
          ) : (
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>זמן</th>
                  <th>סוג</th>
                  <th>סטטוס</th>
                  <th>עובד</th>
                  <th>מדפסת</th>
                  <th>פרטים</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const showItemsButton = log.kind !== 'receipt_print' && log.items.length > 0;
                  return (
                    <tr key={log.id} data-testid={`print-log-row-${log.id}`}>
                      <td>{formatLogTime(log.occurredAt)}</td>
                      <td>
                        <span className={classes.kindCell}>
                          <FontAwesomeIcon
                            icon={getKindIcon(log.kind)}
                            className={`${classes.kindIcon} ${
                              log.kind === 'red_fire'
                                ? classes.kindIconRed
                                : log.kind === 'green_fire'
                                  ? classes.kindIconGreen
                                  : log.kind === 'reprint'
                                    ? classes.kindIconReprint
                                    : classes.kindIconReceipt
                            }`}
                          />
                          <span>{getKindLabel(log.kind)}</span>
                        </span>
                      </td>
                      <td className={`${classes.statusCell} ${getStatusClass(log.status, log.kind)}`}>{getStatusLabel(log.status, log.kind)}</td>
                      <td>{log.workerName ?? '-'}</td>
                      <td>{log.printerName ?? '-'}</td>
                      <td>
                        <div className={classes.detailsCell}>
                          {showItemsButton ? (
                            <UnstyledButton
                              component="button"
                              type="button"
                              onClick={() => onOpenItems(log)}
                              className={classes.detailsLink}
                              data-testid={`print-log-items-${log.id}`}
                            >
                              {getLineCountLabel(log.lineCount)}
                            </UnstyledButton>
                          ) : (
                            <span className={classes.detailsLabel}>
                              {log.kind === 'receipt_print' ? 'חשבון' : getLineCountLabel(log.lineCount)}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export const OrderPrintLogModal = React.memo(OrderPrintLogModalComponent);
